import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { Avatar, AvatarSizes } from '@inno-ui/avatar'
import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import { CheckmarkIcon, MultiplyIcon } from '@inno-ui/icons'
import dayjs from 'dayjs'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import {
  useAcknowledgeMaterialMutation,
  useMarkSeenMutation,
  useNewProductUpdatesQuery,
  useSnoozeUpdatesMutation
} from 'shared/api/productUpdatesApi'
import { VideoPlayer } from 'shared/components/VideoPlayer'
import { getDateLibLocale } from 'shared/utils/getDateLibLocale'

import styles from './ProductUpdates.module.scss'

export const ProductUpdates: FC = () => {
  const [isVideoCompleted, setIsVideoCompleted] = useState(false)
  const [showProductUpdates, setShowProductUpdates] = useState(false)

  const { data } = useNewProductUpdatesQuery()
  const { mutate: acknowledgeMaterial } = useAcknowledgeMaterialMutation()
  const { mutate: markSeen } = useMarkSeenMutation()
  const { mutate: snoozeUpdates } = useSnoozeUpdatesMutation()

  // Track which materials we've already reported as "seen" so we ping the backend
  // at most once per material, regardless of re-renders / refetches / strict mode.
  const seenPingedRef = useRef<Set<string>>(new Set())

  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'product_updates'
  })

  const currentUpdate = data?.[0]

  useEffect(() => {
    if (data && data?.length > 0) {
      setShowProductUpdates(true)
    }
  }, [data])

  // Reset the watch gate whenever a different material becomes the current one,
  // so finishing one video doesn't leave the next material's acknowledge unlocked.
  useEffect(() => {
    setIsVideoCompleted(false)
  }, [currentUpdate?.material_id])

  // Record that the user saw a material the moment its modal is actually displayed,
  // even if they later click "Remind me later" instead of acknowledging. Fires once
  // per material (guarded by seenPingedRef).
  useEffect(() => {
    const materialId = currentUpdate?.material_id
    if (!showProductUpdates || !materialId) {
      return
    }
    if (seenPingedRef.current.has(materialId)) {
      return
    }
    seenPingedRef.current.add(materialId)
    markSeen(materialId)
  }, [showProductUpdates, currentUpdate?.material_id, markSeen])

  const handleVideoEnded = () => {
    setIsVideoCompleted(true)
  }

  // Fallback so a broken or blocked video (expired URL, CORS, codec, network) can
  // never permanently disable acknowledge: unlock once the viewer reaches near the
  // end, or immediately if the video errors out.
  const handleVideoProgress = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget
    if (video.duration > 0 && video.currentTime / video.duration >= 0.95) {
      setIsVideoCompleted(true)
    }
  }
  // Dismissing the modal (Remind me later, the ✕ button, or Escape) snoozes so it
  // doesn't immediately reopen on the next pending-acknowledgements refetch.
  const handleClose = useCallback(() => {
    snoozeUpdates()
    setShowProductUpdates(false)
  }, [snoozeUpdates])

  const onComplete = (materialId: string) => {
    acknowledgeMaterial(materialId)
    setShowProductUpdates(false)
  }

  // Allow closing with the Escape key, so the modal is always dismissible even
  // when its footer buttons are off-screen on short viewports (e.g. 13" laptops).
  useEffect(() => {
    if (!showProductUpdates) {
      return
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showProductUpdates, handleClose])

  if (!showProductUpdates || !currentUpdate) return null
  return (
    <motion.div
      initial={{
        opacity: 0
      }}
      animate={{
        opacity: 1
      }}
      exit={{
        opacity: 0
      }}
      className={styles.productUpdatesWrapper}
    >
      <div className={styles.productUpdates}>
        <div className={styles.mainWrapper}>
          <AnimatePresence mode='wait'>
            <header key='header'>
              <p>{t('title')}</p>
              <h1>{currentUpdate.material_name}</h1>
              <div className={styles.closeButton}>
                <Button
                  variant={ButtonVariants.Ghost}
                  size={ButtonSizes.M}
                  icon={MultiplyIcon}
                  onClick={handleClose}
                  aria-label={t('close_button')}
                />
              </div>
            </header>
            <motion.div
              key='page'
              className={styles.page}
              initial={{
                opacity: 0,
                scale: 0.95
              }}
              animate={{
                opacity: 1,
                scale: 1
              }}
              exit={{
                opacity: 0,
                scale: 0.95
              }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className={styles.content}>
                <div className={styles.videoSection}>
                  {currentUpdate.material_video_url && (
                    <div className={styles.videoPlayerContainer}>
                      <VideoPlayer
                        className={styles.videoPlayer}
                        videoClassName={styles.video}
                        src={currentUpdate.material_video_url}
                        features={{ seek: false }}
                        preload='metadata'
                        poster={currentUpdate.thumbnail_url}
                        labels={{ video: currentUpdate.material_name }}
                        onEnded={handleVideoEnded}
                        onError={handleVideoEnded}
                        onTimeUpdate={handleVideoProgress}
                        onLoadedMetadata={e => {
                          e.currentTarget.playbackRate = 1.25
                        }}
                      />
                    </div>
                  )}

                  <div className={styles.videoInfo}>
                    <div className={styles.authorInfo}>
                      <Avatar
                        url={currentUpdate.material_publisher_avatar}
                        size={AvatarSizes.XL}
                      />
                      <div>
                        <div className={styles.authorName}>
                          {currentUpdate.material_publisher_name}
                        </div>
                        <div className={styles.authorTitle}>
                          {currentUpdate.material_publisher_title}
                        </div>
                      </div>
                      <div className={styles.date}>
                        {dayjs(currentUpdate.published_at)
                          .locale(getDateLibLocale(i18n.language))
                          .format('YYYY-MM-DD')}
                      </div>
                    </div>

                    <div className={styles.description}>
                      <ReactMarkdown
                        remarkPlugins={[remarkBreaks]}
                        rehypePlugins={[]}
                      >
                        {currentUpdate.material_description}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            <footer key='footer'>
              <div className={styles.buttonGroup}>
                <Button
                  variant={ButtonVariants.Dark}
                  color={ButtonColors.Green}
                  size={ButtonSizes.M}
                  disabled={
                    currentUpdate.material_video_url ? !isVideoCompleted : false
                  }
                  label={t('acknowledge_button')}
                  onClick={() => onComplete(currentUpdate.material_id)}
                  icon={CheckmarkIcon}
                />
                <Button
                  variant={ButtonVariants.Light}
                  color={ButtonColors.Blue}
                  size={ButtonSizes.M}
                  label={t('remind_me_later_button')}
                  onClick={handleClose}
                />
              </div>
            </footer>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}
